import React from 'react';
import { PropTypes } from 'prop-types';
import _ from 'underscore';
import SmoothScroll from 'smooth-scroll';
import classnames from 'classnames';
import { Utils } from "poly-js-utils";
import {
    emailPatternValidation,
    formatCheckboxData,
    getElementOffsetTop,
    getNavbarHeight,
    Style
} from '../helpers.jsx';
import { connectToJSContext, parseJsonSafe } from '../utils.jsx';
import {
    contactSelectorIndex,
    fieldDefaultMaxLength,
    phoneNumberIsValid,
    requiredContactMethods,
    shouldShowContactSelector
} from './selectors';
import { CONTACT_FORM } from '../constants';
import Recaptcha from "../recaptcha/Recaptcha";
import LazyImg from '../lazy_img/index.jsx';
import ImageUploadWidget from '../image_upload_widget/index.jsx';
import { AMPComponent } from "../amp";

// Contact Form Wrapper
/* eslint-disable jsx-a11y/anchor-has-content */
const ContactFormWrapper = props => (
    <section id="contact-form" className="hs-contact" ref={props.containerRef}>
        <div className="container">
            {props.children}
        </div>
    </section>
);

// Contact Input Generator
const ContactInput = props => {
    const {
        fieldData: {
            fieldType, name: fieldName, label,
            fieldOptions: {
                display,
                placeholder,
                maxLength,
                options
            }
        },
        errorMsgs,
        handleFocusOut,
        required,
        valid,
        value
    } = props;

    const className = classnames({
        'form-control': true,
        'validation-error': !valid
    });

    const inputProps = {
        placeholder,
        maxLength,
        className,
        onChange: props.handleChange
    };

    const errorMsg = errorMsgs[fieldName + "_error"] || CONTACT_FORM.ERROR_MSG;

    return (
        <div className="form-group">
            <ContactFormFields
                label={label}
                required={required}
                inputProps={inputProps}
                fieldType={fieldType}
                fieldDisplay={display}
                fieldName={fieldName}
                selectOptions={options}
                value={value}
                handleFocusOut={handleFocusOut}
                errorMsg={errorMsg}
                valid={valid}
            />
        </div>
    );
};

const ContactFormLabel = ({ label, elementType, fieldName, required, inputProps }) => {
    const innerLabel = (
        <span>
            {`${label} `}
            {fieldName === 'phone' && inputProps && inputProps.placeholder &&
                <span className="sr-only">{inputProps.placeholder}</span>}
            {required && <sup>*</sup>}
        </span>
    );

    switch (elementType) {
        case 'legend':
            return (
                <legend className="c-contact-form__legend">
                    {innerLabel}
                </legend>
            );
        default:
            return (
                <label htmlFor={fieldName} className="control-label">
                    {innerLabel}
                </label>
            );
    }
};

/*
 * Renders a contact method selector
 */
const ContactMethodSelector = ({ checked, labels, onChange, preferred }) => (
    <fieldset className="col-xs-12 hs-contact__method-selector">
        <legend>{preferred}<sup>*</sup></legend>
        <div className="hs-contact__method-selector-item">
            <input
                checked={checked}
                type="radio"
                id="contact-method-selector-email"
                name="method-select"
                onChange={onChange}
                value="email"
            />
            <label htmlFor="contact-method-selector-email">{labels.email}</label>
        </div>
        <div className="hs-contact__method-selector-item">
            <input
                checked={!checked}
                type="radio"
                id="contact-method-selector-phone"
                name="method-select"
                onChange={onChange}
                value="phone"
            />
            <label htmlFor="contact-method-selector-phone">{labels.phone}</label>
        </div>
    </fieldset >
);

ContactMethodSelector.defaultProps = {
    labels: { email: 'Email ', phone: 'Phone ' }
};

const RelateOptInDisclaimer = ({ isOptInMandatory, optInText, onRelateOptInChange, isInvalid }) => (
    <div id="relate-opt-in-container" className={classnames({ 'validation-error': isInvalid })}>
        <div className="opt-in-box">
            <input
                type="checkbox"
                id="relate-opt-in"
                name="relate-opt-in"
                required={isOptInMandatory}
                onChange={onRelateOptInChange}
            />
            {isOptInMandatory &&
                <sup>&nbsp;*</sup>
            }
        </div>
        <div className="opt-in-text">{optInText.split('\n').map((item, key) => {
            return <span key={key}>{item}<br /></span>;
        })}
        </div>
    </div>
);

/**
 * Renders a Contact Form field
 */
const ContactFormFields = props => {
    const { inputProps, label, required, fieldName, fieldType, handleFocusOut, value, errorMsg, valid } = props;
    switch (fieldType) {
        case 'textarea':
            return (
                <div>
                    <ContactFormLabel
                        label={label}
                        required={required}
                        fieldName={fieldName}
                    />
                    <textarea
                        id={fieldName}
                        type={fieldType}
                        onBlur={handleFocusOut}
                        value={value}
                        aria-required={required}
                        required={required}
                        {...inputProps}
                    />
                    {!valid &&
                        <p role="alert"
                            className={`error-message error-message-${fieldName}`}
                        >
                            <span className="sr-only">{label} </span>{errorMsg}
                        </p>
                    }
                </div>
            );
        case 'select':
            return (
                <ContactFormSelect
                    {...props}
                />
            );
        case 'image':
            return (
                <div>
                    <ContactFormLabel
                        label={label}
                        required={required}
                        fieldName={fieldName}
                    />
                    <ImageUploadWidget
                        onChange={inputProps.onChange}
                        isValid={valid}
                        imageUrl={value}
                    />
                    {!valid &&
                        <p role="alert"
                            className={`error-message error-message-${fieldName}`}
                        >
                            <span className="sr-only">{label} </span>{errorMsg}
                        </p>
                    }
                </div>
            );
        default:
            return (
                <div>
                    <ContactFormLabel
                        label={label}
                        required={required}
                        fieldName={fieldName}
                        inputProps={inputProps}
                    />
                    <input
                        id={fieldName}
                        type={fieldType}
                        value={value}
                        onBlur={handleFocusOut}
                        aria-required={required}
                        required={required}
                        {...inputProps}
                    />
                    {!valid &&
                        <p role="alert"
                            className={`error-message error-message-${fieldName}`}
                        >
                            <span className="sr-only">{label} </span>{errorMsg}
                        </p>
                    }
                </div>
            );
    }
};

/**
 * Renders a collection of selection inputs
 * Types include 'radio', 'checkbox', 'dropdown'
 */
const ContactFormSelect = props => {
    const { selectOptions, fieldDisplay, fieldName, inputProps, label, valid, required, errorMsg } = props;
    switch (fieldDisplay) {
        case 'radio':
        case 'checkbox':
            const groupedOptions = _.groupBy(selectOptions, (option, i) =>
                Math.floor(i / 3)
            );
            return (
                <fieldset id={fieldName}>
                    <ContactFormLabel
                        label={label}
                        elementType="legend"
                    />
                    {_.map(groupedOptions, (options, i) => (
                        <div className="row" key={i}>
                            {options.map((option) => (
                                <ContactFormSelectBox
                                    key={option.name}
                                    fieldLabel={option.label}
                                    fieldKey={option.name}
                                    {...props}
                                />
                            ))}
                        </div>
                    ))}
                    {!valid &&
                        <p role="alert"
                            className={`error-message error-message-${fieldName}`}
                        >
                            <span className="sr-only">{label} </span>{errorMsg}
                        </p>
                    }
                </fieldset>
            );
        case 'dropdown':
            return (
                <div>
                    <ContactFormLabel
                        label={label}
                        fieldName={fieldName}
                    />
                    <select
                        id={fieldName}
                        aria-required={required}
                        required={required}
                        {...inputProps}
                    >
                        {selectOptions.map((option) => (
                            <option value={option.name} key={option.name}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                    {!valid &&
                        <p role="alert"
                            className={`error-message error-message-${fieldName}`}
                        >
                            <span className="sr-only">{label} </span>{errorMsg}
                        </p>
                    }
                </div>
            );
        default:
            return (<div />);
    }
};

/**
 * Renders an individual radio or checkbox for an input field type.
 * Should be called from within a loop.
 */
const ContactFormSelectBox = ({ fieldDisplay, fieldName, fieldKey, fieldLabel, inputProps }) => (
    <div className={`hs-contact__${fieldDisplay}-container`}>
        <input
            type={fieldDisplay}
            name={fieldName}
            value={fieldLabel}
            id={fieldKey}
            {...inputProps}
        />
        <label htmlFor={fieldKey} className={`hs-contact__${fieldDisplay} ${fieldDisplay}-inline`}>
            {fieldLabel}
        </label>
    </div>
);

const SuccessMessage = ({ title, message }) => (
    <div role="alert" className="section-header success-message">
        <h2>{title}</h2>
        <p>{message}</p>
    </div>
);

const ErrorMessage = ({ title, message }) => (
    <div role="alert" className="section-header error-message">
        <h2>{title}</h2>
        <p>{message}</p>
    </div>
);

class ContactForm extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            checked: false,
            contactOptions: this.props.contactOptions,
            fields: [],
            preferredContactMethod: '',
            submitted: false,
            submissionError: false,
            validating: false,
            recaptcha: {
                isValid: false,
                hasTouched: false,
                g_recaptcha_response: null
            },
            relateOptIn: {
                isValid: false,
                checked: false,
                isDisplay: false,
                consentMessage: null,
                attestationText: null,
                submitted: false,
                submissionError: false
            },
            wellsFargoEmail: {
                submitted: false,
                submissionError: false
            },
            relateMessage: {
                submitted: false,
                submissionError: false
            }
        };
        this.state.fields = this.getInitialFields(this.props.contactFields);
    }

    componentDidMount() {
        this.scroll = new SmoothScroll();
        this.checkRelateOptIn();
    }

    checkRelateOptIn = () => {
        const { relateOptIn } = this.props;
        const isRelateEnabled = Boolean(relateOptIn.optInText);
        if (isRelateEnabled) {
            this.fetchRelateOption();
        }
    };

    fetchRelateOption = () => {
        const { relateOptIn } = this.props;
        const request = new XMLHttpRequest();
        request.open('GET', relateOptIn.url, true);
        request.onload = () => {
            if (request.status >= 200 && request.status < 400) {
                const { phone_number, attestation_text, consent_message } = JSON.parse(request.response);
                if (phone_number) {
                    this.setState((state) => ({
                        relateOptIn: {
                            ...state.relateOptIn,
                            isDisplay: true,
                            consentMessage: consent_message,
                            attestationText: attestation_text
                        }
                    }));
                }
            }
        };
        request.send();
    };

    isRelateOptionEnabledAndChecked = () => {
        const { relateOptIn, checked } = this.state;
        return relateOptIn.isDisplay && relateOptIn.checked && !checked;
    };

    isRelateOptInEnabled = () => this.state.relateOptIn.isDisplay && this.props.relateOptIn.optInText;

    isShowSuccessMessage = () => {
        return this.state.submitted
            && (!this.isRelateOptionEnabledAndChecked() || this.state.relateOptIn.submitted)
            && (!this.props.isWellsFargo || this.state.wellsFargoEmail.submitted)
            && (!this.props.relateMessage.isEnabled || this.state.relateMessage.submitted);
    };

    isShowErrorMessage = () => {
        return this.state.relateOptIn.submissionError
            || this.state.wellsFargoEmail.submissionError
            || this.state.relateMessage.submissionError;
    };

    updateContactMethod = (e) => {
        const selectedContactMethod = e.target.value;
        const unselectedContactMethod = selectedContactMethod === 'phone' ? 'email' : 'phone';
        const updatedFields = [...this.state.fields];

        for (const field of updatedFields) {
            if (field.name === selectedContactMethod) {
                field.required = true;
            } else if (field.name === unselectedContactMethod) {
                field.required = false;
            }

            field.valid = this.customValidation(field.type, field.value, field.required);
        }

        this.setState(({ checked }) => ({
            checked: !checked,
            fields: updatedFields,
            preferredContactMethod: selectedContactMethod
        }));
    };

    getInitialFields(fields) {
        return fields.map((field) => {
            return {
                display: field.fieldOptions && field.fieldOptions.display,
                label: field.label,
                name: field.name,
                required: field.isRequired,
                type: field.fieldType,
                value: "",
                valid: !field.isRequired
            };
        });
    }

    // Updates multiple fields in state at once
    // Accepts an array of objects in the form of
    // { index: int, data: object }
    updateFields(fieldData) {
        if (fieldData) {
            fieldData = _.isArray(fieldData) ? fieldData : [fieldData];
            let newFields = this.state.fields;

            _.each(fieldData, (field) => {
                const updatedField = Object.assign({}, newFields[field.index], field.data);
                newFields = [
                    ...newFields.slice(0, field.index),
                    updatedField,
                    ...newFields.slice(field.index + 1)
                ];
            });

            this.setState({
                fields: newFields
            });
        }
    }

    handleChange(e, index) {
        let { value } = e.target;
        const fieldData = this.state.fields[index];
        const required = fieldData.required || false;

        if (fieldData.type === 'select' && fieldData.display === 'checkbox') {
            value = formatCheckboxData(value, fieldData.value);
        }

        const valid = e.target.checkValidity() && this.customValidation(fieldData.type, value, required);

        if (value !== this.state.fields[index].value) {
            this.updateFields({ index, data: { value, valid } });
        }
    }

    customValidation(typeAttr, value, required) {
        if (!required && !value) {
            return true;
        } else if (required && !value) {
            return false;
        }

        switch (typeAttr) {
            case 'email':
                return emailPatternValidation(value);
            case 'tel':
                return phoneNumberIsValid(this.props.acceptedCountryCodes, value);
            default:
                return true;
        }
    }

    displayErrorSummary = () => {
        if (this.props.isContactFormValidationSummaryDisplayed && this.props.contactFormValidationSummaryTextFormat) {
            const validationTextFormat = this.props.contactFormValidationSummaryTextFormat;
            this.setState((state) => {
                let errorCount = _.countBy(state.fields, 'valid')[false] || 0;
                if (this.hasRecaptchaErrors()) {
                    errorCount++;
                }
                if (!this.isRelateOptInValid()) {
                    errorCount++;
                }
                return {
                    validationSummary: errorCount ? validationTextFormat.replace('%ERROR_COUNT%', errorCount) : null
                };
            });
        }
    }

    handleSubmit = (e) => {
        e.preventDefault();
        this.setState((state) => {
            return {
                recaptcha: { ...state.recaptcha, hasTouched: true }
            };
        });
        if (this.hasErrors()) {
            this.scrollToTopOfForm();
            this.displayErrorSummary();
            this.setState({ validating: true, submissionError: true });
            if (this.contactFormRef) {
                setTimeout(() => {
                    const element = this.contactFormRef.getElementsByClassName('validation-summary');
                    if (element && element[0]) {
                        element[0].focus();
                    }
                }, 1);
            }
            return;
        }
        const postData = this.createPostData();
        const noCache = `?cache=${Math.random()}`;
        const url = `${this.props.orgLink}api/contact/${this.props.slug}/${noCache}`;

        const request = new XMLHttpRequest();

        request.onload = () => {
            if (request.status >= 200 && request.status < 300) {
                this.setState(() => ({
                    validating: false,
                    submitted: true,
                    submissionError: false
                }));
                this.scrollToTopOfForm();
                if (this.props.isWellsFargo) {
                    this.postWellsFargoContactEmail(postData);
                }
                const { contact_id: contactId } = parseJsonSafe(request.responseText) || {};
                if (!contactId) {
                    // eslint-disable-next-line no-console
                    console.error('No contact_id retrieved from contact request, skipping relate');
                    return;
                }
                if (this.isRelateOptionEnabledAndChecked()) {
                    this.postRelateOptIn(postData);
                    if (this.props.relateMessage.isEnabled) {
                        this.postContactFormDataAsRelateMessage(postData, contactId);
                    }
                }
            } else {
                const res = JSON.parse(request.responseText);
                const errorData = _.isString(res.error) ? JSON.parse(res.error) : res.error;
                const fieldsToUpdate = _.map(errorData, (value, key) => {
                    const index = _.findIndex(this.state.fields, { name: key });
                    return { index, data: { valid: false } };
                });
                this.updateFields(fieldsToUpdate);
                this.setState({ submissionError: true, validating: true });
                this.scrollToTopOfForm();
            }
        };
        request.onerror = () => {
            this.setState({ submissionError: true, validating: false });
            this.scrollToTopOfForm();
        };
        request.open('POST', url, true);
        request.setRequestHeader('Access-Control-Allow-Origin', '*');
        request.setRequestHeader('Content-Type', 'application/json; charset=utf-8');
        request.send(JSON.stringify(postData));
    };

    postWellsFargoContactEmail = (postData) => {
        const { phone, email, name } = postData;
        const wellsFargoEmailPostData = {
            toAddress: email,
            templateNameHtml: 'contactEmailTemplate.html',
            templateNameText: 'contactEmailTemplate.html',
            subject: 'Jennifer Yang received your request to get in touch',
            templateData: {
                name,
                emailAddress: email,
                phoneNumber: phone || '-'
            }
        };
        const request = new XMLHttpRequest();
        request.onload = () => {
            const response = JSON.parse(request.responseText);
            if (request.status >= 200 && request.status < 300 && !response.error) {
                this.setState((state) => ({
                    wellsFargoEmail: {
                        ...state.wellsFargoEmail,
                        submitted: true,
                        submissionError: false
                    }
                }));
            } else {
                this.setState((state) => ({
                    wellsFargoEmail: {
                        ...state.wellsFargoEmail,
                        submissionError: true
                    }
                }));
            }
        };
        request.open('POST', 'https://9crfaa42nb.execute-api.us-west-2.amazonaws.com/prod/sendmail', true);
        request.setRequestHeader('Access-Control-Allow-Origin', '*');
        request.setRequestHeader('Content-Type', 'application/json; charset=utf-8');
        request.send(JSON.stringify(wellsFargoEmailPostData));
    }

    postRelateOptIn = (postData) => {
        const { phone, email, name } = postData;
        const { FIRST_NAME, LAST_NAME } = CONTACT_FORM.CUSTOM_FIELDS;
        const { relateOptIn } = this.state;
        const relatePostData = {
            consent_message: relateOptIn.consentMessage,
            attestation_text: relateOptIn.attestationText,
            first_name: postData[FIRST_NAME] || name.split(' ').slice(0, -1).join(' '),
            last_name: postData[LAST_NAME] || name.split(' ').pop(),
            phone_number: phone,
            email_address: email
        };
        const request = new XMLHttpRequest();
        request.onload = () => {
            const response = JSON.parse(request.responseText);
            if (request.status >= 200 && request.status < 300 && !response.error) {
                this.setState((state) => ({
                    relateOptIn: {
                        ...state.relateOptIn,
                        submitted: true,
                        submissionError: false
                    }
                }));
            } else {
                this.setState((state) => ({
                    relateOptIn: {
                        ...state.relateOptIn,
                        submissionError: true
                    }
                }));
            }
        };
        request.onerror = () => {
            this.setState((state) => ({
                relateOptIn: {
                    ...state.relateOptIn,
                    submissionError: true
                }
            }));
        };
        request.open('POST', this.props.relateOptIn.url, true);
        request.setRequestHeader('Access-Control-Allow-Origin', '*');
        request.setRequestHeader('Content-Type', 'application/json; charset=utf-8');
        request.send(JSON.stringify(relatePostData));
    };

    postContactFormDataAsRelateMessage = (postData, contactId) => {
        const {
            phone,
            name
        } = postData;
        const getCustomFieldValue = (name) => {
            const [{ value } = {}] = this.state.fields.filter((field) => name === field.name);
            return value;
        };
        const getRelateMessageRow = (label, value) => {
            if (value) {
                return `${label}: ${value}\n`;
            }
            return '';
        };
        const mediaUrls = [];
        const idImageUrl = getCustomFieldValue('id_image');
        const utilityBillImageUrl = getCustomFieldValue('utility_bill_image');
        if (idImageUrl) {
            mediaUrls.push(idImageUrl);
        }
        if (utilityBillImageUrl) {
            mediaUrls.push(utilityBillImageUrl);
        }
        const dateOfBirth = getCustomFieldValue('date_of_birth');
        const address = getCustomFieldValue('address');
        const message = getCustomFieldValue('message');
        const comments = getCustomFieldValue('comments');

        const customerPhone = getCustomFieldValue('phone');
        const email = getCustomFieldValue('email');
        const state = getCustomFieldValue('state');
        const relatePostData = {
            to_number: phone,
            body: `[Lead Form / Site]\n`
                + `${getRelateMessageRow('Name', name)}`
                + `${getRelateMessageRow('Date of birth', dateOfBirth)}`
                + `${getRelateMessageRow('Email', email)}`
                + `${getRelateMessageRow('Phone', customerPhone)}`
                + `${getRelateMessageRow('State', state)}`
                + `${getRelateMessageRow('Address', address)}`
                + `${getRelateMessageRow('Terms Agree Checkbox', 'True')}`
                + `${getRelateMessageRow('Agreed Terms', this.props.relateOptIn.optInText)}`
                + `${getRelateMessageRow('Message', message || comments)}`
            ,
            direction: 'inbound',
            media_urls: mediaUrls,
            contact_id: contactId
        };
        const request = new XMLHttpRequest();
        request.onload = () => {
            const response = JSON.parse(request.responseText);
            if (request.status >= 200 && request.status < 300 && !response.error) {
                this.setState((state) => ({
                    relateMessage: {
                        ...state.relateMessage,
                        submitted: true,
                        submissionError: false
                    }
                }));
            } else {
                this.setState((state) => ({
                    relateMessage: {
                        ...state.relateMessage,
                        submissionError: true
                    }
                }));
            }
        };
        request.onerror = () => {
            this.setState((state) => ({
                relateMessage: {
                    ...state.relateMessage,
                    submissionError: true
                }
            }));
        };
        request.open('POST', this.props.relateMessage.url, true);
        request.setRequestHeader('Access-Control-Allow-Origin', '*');
        request.setRequestHeader('Content-Type', 'application/json; charset=utf-8');
        request.send(JSON.stringify(relatePostData));
    };

    createPostData() {
        const { rsvpEventDetails } = this.props;
        const postData = {
            custom_data: {}
        };
        const customFields = [];
        const nameValues = {};

        const { FIRST_NAME, LAST_NAME } = CONTACT_FORM.CUSTOM_FIELDS;

        const preferredContactMethod = {
            label: 'Preferred Method of Contact',
            value: this.state.preferredContactMethod || 'phone'
        };

        this.state.fields.forEach((field) => {
            const name = field.name;
            const label = field.label;
            const value = _.isArray(field.value) ? field.value.join(', ') : field.value;

            if (name === FIRST_NAME || name === LAST_NAME) {
                nameValues[name] = value;
                return;
            }
            if (CONTACT_FORM.STANDARD_FIELDS.includes(name)) {
                postData[name] = value;
            }
            else {
                customFields.push({ label, value });
            }
        });

        if (this.props.isContactFormRecaptchaEnabled) {
            postData['g_recaptcha_response'] = this.state.recaptcha.g_recaptcha_response;
        }

        // Temporary Fix: push preferredContactMethod constructed as a field
        if (this.props.contactRequirement) {
            customFields.push(preferredContactMethod);
        }

        // Temporary Fix: Combine First Name + Last Name
        if (!postData['name'] && (nameValues[FIRST_NAME] && nameValues[LAST_NAME])) {
            postData['name'] = `${nameValues[FIRST_NAME]} ${nameValues[LAST_NAME]}`;
        }

        if (this.props.relateOptIn.optInText) {
            postData['relateOptInChecked'] = this.state.relateOptIn.checked;
        }

        const customData = [];
        if (rsvpEventDetails) {
            customData.push({
                title: CONTACT_FORM.EVENT_CUSTOM_DATA.TITLE,
                description: CONTACT_FORM.EVENT_CUSTOM_DATA.DESCRIPTION,
                fields: [{
                    label: 'Event name',
                    value: rsvpEventDetails.eventName
                }, {
                    label: 'Event date',
                    value: rsvpEventDetails.eventDate
                }]
            });
        }
        customData.push({
            title: CONTACT_FORM.CUSTOM_POST_DATA.TITLE,
            description: CONTACT_FORM.CUSTOM_POST_DATA.DESCRIPTION,
            fields: customFields
        });
        postData.custom_data = customData;
        return postData;
    }

    handleFocusOut(e, index, required) {
        e.target.value = e.target.value.trim();

        // Trigger our onChange event
        this.handleChange(e, index, required);
    }

    hasErrors = () => {
        return !_.all(this.state.fields, 'valid') || this.hasRecaptchaErrors() || !this.isRelateOptInValid();
    };

    onRelateOptInChange = (e) => {
        const isChecked = e.target.checked;

        // Email is required in optin
        const updatedFields = [...this.state.fields];
        const emailField = updatedFields.filter((field) =>
            field.name === 'email'
        )[0];
        emailField.required = isChecked;
        emailField.valid = this.customValidation(emailField.type, emailField.value, emailField.required);

        this.setState((state) => ({
            fields: updatedFields,
            relateOptIn: {
                ...state.relateOptIn,
                checked: isChecked
            }
        }));
    };

    isRelateOptInValid = () => {
        const { relateOptIn } = this.props;
        const isRelateEnabled = this.isRelateOptInEnabled();

        if (!isRelateEnabled || this.state.checked) {
            return true;
        }

        if (relateOptIn.isOptInMandatory) {
            return this.state.relateOptIn.checked;
        }

        return true;
    }

    hasRecaptchaErrors = () => {
        return this.props.isContactFormRecaptchaEnabled && (
            (this.state.recaptcha.hasTouched && !this.state.recaptcha.isValid) ||
            !this.state.recaptcha.hasTouched
        );
    };

    recaptchaIsValidHandler = (g_recaptcha_response) => {
        this.setState(() => {
            return {
                recaptcha: {
                    isValid: true,
                    hasTouched: true,
                    g_recaptcha_response
                }
            };
        });
    };

    recaptchaIsInvalidHandler = () => {
        this.setState(() => {
            return {
                recaptcha: {
                    isValid: false,
                    hasTouched: true
                }
            };
        });
    };

    scrollToTopOfForm = () => {
        const scrollToY = getElementOffsetTop(this._containerNode) - getNavbarHeight();
        const speed = 1;

        this.scroll.animateScroll(scrollToY, null, { speed });
    };

    setContainerRef = (node) => {
        this._containerNode = node;
    };

    render() {
        const showSelector = shouldShowContactSelector(this.props.contactRequirement, this.state.fields);
        // isPhoneRequiredAndSelected
        const showRelateOptIn = this.isRelateOptInEnabled() && !this.state.checked;
        const isLeftImage = this.state.contactOptions.orientation === "left";
        const btnStyle = this.props.contactOptions.contactBtn;
        const formContainerClass = classnames('contact-form__container', {
            'orientation-left': isLeftImage,
            'contact-form__container--reverse orientation-right': !isLeftImage
        });

        return (
            <ContactFormWrapper containerRef={this.setContainerRef}>
                <div className={formContainerClass}
                    style={{
                        backgroundColor: this.state.contactOptions.backgroundColor,
                        color: this.state.contactOptions.fontColor,
                        fontSize: this.state.contactOptions.labelSize
                    }}
                >
                    {this.props.contactOptions.image &&
                        <LazyImg
                            isBackground={true}
                            src={this.props.contactOptions.image}
                            className="contact-form__image"
                            alt={this.props.contactOptions.altText}
                        />
                    }
                    <div className="contact-form__form-wrapper">
                        <div className={this.isShowSuccessMessage()
                            || this.isShowErrorMessage() ? 'c-contact-form--invisible' : ''}
                        >
                            <div className="section-header">
                                <h2 style={{ fontSize: this.state.contactOptions.titleSize }}>
                                    {this.props.contactLabel}
                                </h2>
                                { !! this.props.contactFormSubtitle &&
                                    <p className="contact-form__subtitle">{this.props.contactFormSubtitle}</p>
                                }
                                <p><sup>*</sup> {this.props.formRequiredCue}</p>
                            </div>
                            <form className="contact-form clearfix"
                                noValidate
                                onSubmit={this.handleSubmit}
                                ref={instance => this.contactFormRef = instance}
                            >
                                {this.props.isContactFormValidationSummaryDisplayed &&
                                    this.props.contactFormValidationSummaryTextFormat &&
                                    this.state.validationSummary && (
                                    // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
                                    <p role="alert" tabIndex="0"
                                        className="validation-summary"
                                    >
                                        {this.state.validationSummary}
                                    </p>
                                )}
                                {this.state.fields.map((field, i) => (
                                    <div className="form-group-input-wrapper" key={i}>
                                        {showSelector && i === this.props.contactSelectorIndex &&
                                            <ContactMethodSelector
                                                labels={this.props.contactMethodLabels}
                                                checked={this.state.checked}
                                                onChange={this.updateContactMethod}
                                                preferred={this.props.preferredContactMethodLabel}
                                            />
                                        }
                                        <ContactInput
                                            value={field.value}
                                            valid={!this.state.validating || field.valid}
                                            key={i}
                                            handleChange={(e) => this.handleChange(e, i,
                                                this.props.contactFields[i].isRequired)}
                                            handleFocusOut={(e) => this.handleFocusOut(e, i, field.isRequired)}
                                            required={this.state.fields[i].required}
                                            fieldData={this.props.contactFields[i]}
                                            errorMsgs={this.props.errorMsgs}
                                        />
                                    </div>
                                ))}
                                <Style>{`
                                    #contact-form .contact-btn:hover {
                                        background-color: ${btnStyle && btnStyle.backgroundHoverColor} !important;
                                        color: ${btnStyle && btnStyle.textHoverColor} !important;
                                    }
                                `}</Style>
                                {this.props.isContactFormRecaptchaEnabled && (
                                    <Recaptcha
                                        siteKey={window.JSContext.google_recaptcha_sitekey}
                                        recaptcha={this.state.recaptcha}
                                        recaptchaIsValidHandler={this.recaptchaIsValidHandler}
                                        recaptchaIsInvalidHandler={this.recaptchaIsInvalidHandler}
                                    />
                                )}
                                {showRelateOptIn &&
                                    <div className="form-group-input-wrapper">
                                        <RelateOptInDisclaimer
                                            optInText={this.props.relateOptIn.optInText}
                                            isOptInMandatory={this.props.relateOptIn.isOptInMandatory}
                                            onRelateOptInChange={this.onRelateOptInChange}
                                            relateOptIn={this.state.relateOptIn}
                                            isInvalid={this.state.submissionError && !this.isRelateOptInValid()}
                                        />
                                    </div>
                                }
                                <button
                                    type="submit"
                                    className="btn btn-hs contact-btn"
                                    style={{
                                        backgroundColor: btnStyle && btnStyle.backgroundColor,
                                        fontSize: btnStyle && btnStyle.textSize,
                                        color: btnStyle && btnStyle.textColor
                                    }}
                                    data-click-type="form-submit"
                                    data-form-name="contact"
                                >
                                    {this.props.contactOptions.contactBtnText}
                                </button>
                                {
                                    this.props.disclaimers &&
                                    <p className="c-contact-form__disclaimers">{this.props.disclaimers}</p>
                                }
                            </form>
                        </div>
                        {this.isShowSuccessMessage() &&
                            <SuccessMessage
                                title={this.props.submissionSuccessTitle}
                                message={this.props.submissionSuccess}
                            />
                        }
                        {this.isShowErrorMessage() &&
                            <ErrorMessage
                                title={this.props.generalErrorMessageTitle}
                                message={this.props.generalErrorMessage}
                            />
                        }
                    </div>
                </div>
            </ContactFormWrapper>
        );
    }

}

ContactForm.propTypes = {
    orgLink: PropTypes.string.isRequired,
    slug: PropTypes.string.isRequired,
    disclaimers: PropTypes.string,
    contactFields: PropTypes.arrayOf(PropTypes.shape({
        fieldType: PropTypes.oneOf(CONTACT_FORM.FIELD_TYPES).isRequired,
        label: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        isRequired: PropTypes.bool,
        fieldOptions: PropTypes.shape({
            placeholder: PropTypes.string,
            display: PropTypes.string,
            maxLength: PropTypes.number,
            hidden: PropTypes.bool,
            options: PropTypes.arrayOf(PropTypes.shape({
                label: PropTypes.string.isRequired,
                name: PropTypes.string.isRequired
            }))
        })
    })).isRequired,
    contactLabel: PropTypes.string,
    contactFormLabel: PropTypes.string,
    isContactFormRecaptchaEnabled: PropTypes.bool,
    isContactFormValidationSummaryDisplayed: PropTypes.bool,
    contactFormValidationSummaryTextFormat: PropTypes.string,
    contactOptions: PropTypes.shape({
        image: PropTypes.string,
        altText: PropTypes.string,
        orientation: PropTypes.string.isRequired,
        backgroundColor: PropTypes.string,
        fontColor: PropTypes.string,
        titleSize: PropTypes.string,
        labelSize: PropTypes.string,
        contactBtnText: PropTypes.string,
        contactBtn: PropTypes.shape({
            backgroundColor: PropTypes.string.isRequired,
            backgroundHoverColor: PropTypes.string.isRequired,
            textSize: PropTypes.string.isRequired,
            textColor: PropTypes.string.isRequired,
            textHoverColor: PropTypes.string.isRequired
        })
    }),
    acceptedCountryCodes: PropTypes.arrayOf(PropTypes.string).isRequired,
    formRequiredCue: PropTypes.string
};

ContactForm.defaultProps = {
    contactLabel: CONTACT_FORM.DEFAULT_PROPS.CONTACT_LABEL,
    submissionSuccessTitle: CONTACT_FORM.DEFAULT_PROPS.SUBMISSION_SUCCESS_TITLE,
    submissionSuccess: CONTACT_FORM.DEFAULT_PROPS.SUBMISSION_SUCCESS
};

const contactFieldsProps = state => {
    const contactForm = state.theme_data.contact_form;
    const contactRequirement = state.theme_data.contact_form.contact_requirement &&
        state.theme_data.contact_form.contact_requirement[0];
    let allFieldOptions = {};
    let contactFields;

    if (contactForm) {
        contactFields = (contactForm.fields || []).map(field => {
            const fieldOptions = field.field_options;

            if (fieldOptions) {
                allFieldOptions = {
                    placeholder: fieldOptions.placeholder,
                    display: fieldOptions.display,
                    maxLength: (fieldOptions.max_length && parseInt(fieldOptions.max_length, 10)) ||
                        fieldDefaultMaxLength(field),
                    hidden: Boolean(fieldOptions.hidden && Utils.selectBoxIsTrue(fieldOptions.hidden)),
                    options: (fieldOptions.options || []).map(option => ({
                        label: option.label,
                        name: option.name
                    }))
                };
            }

            return {
                fieldType: field.field_type[0],
                label: field.label,
                name: field.name,
                isRequired: requiredContactMethods(field, contactRequirement),
                fieldOptions: allFieldOptions
            };
        });
    }

    return contactFields;
};

const contactOptionsProps = state => {
    const contactForm = state.theme_data.contact_form;
    let contactOptions, contactBtn, btnOptions;

    if (contactForm) {
        contactBtn = contactForm.contact_cta;

        if (contactBtn) {
            btnOptions = {
                backgroundColor: contactBtn.background_color,
                backgroundHoverColor: contactBtn.hover_color,
                textSize: contactBtn.text_size,
                textColor: contactBtn.text_color,
                textHoverColor: contactBtn.text_hover_color
            };
        }

        contactOptions = {
            image: contactForm.image,
            altText: contactForm.image_alt_text,
            orientation: (contactForm.orientation && contactForm.orientation[0]) || "left",
            backgroundColor: contactForm.background_color,
            fontColor: contactForm.font_color,
            titleSize: contactForm.title_font_size,
            labelSize: contactForm.label_font_size,
            contactBtnText: contactForm.contact_cta_text || CONTACT_FORM.CONTACT_BUTTON_TEXT,
            contactBtn: btnOptions
        };
    }

    return contactOptions;
};

const contactMethodLabelSelector = contactFields => {
    const defaultLabels = { ...ContactMethodSelector.defaultProps.labels };
    return contactFields.reduce((acc, { name, label }) => {
        if (acc.hasOwnProperty(name)) {
            // replace default label with passed in one
            acc[name] = label;
        }
        return acc;
    }, defaultLabels);
};

const getRsvpEventDetails = (state, selectedEventId) => {
    if (!selectedEventId) {
        return;
    }
    const [{ event_name, event_date }] = state.events.filter(
        ({ id }) => String(id) === String(selectedEventId)
    );
    return {
        eventName: event_name,
        eventDate: event_date
    };
};

const mapStateToProps = (state, { selectedEventId }) => {

    const ampComponents = state.theme_data.amp_components || {};
    if (ampComponents && ampComponents.contact_form) {
        return {
            amp: true,
            ...ampComponents.contact_form
        };
    }

    const contactFields = contactFieldsProps(state);
    const contactOptions = contactOptionsProps(state);
    const contactMethodLabels = contactMethodLabelSelector(contactFields);
    const contactRequirement = state.theme_data.contact_form.contact_requirement &&
        state.theme_data.contact_form.contact_requirement[0];

    return {
        orgLink: state.org_link,
        slug: state.slug,
        disclaimers: state.theme_data.contact_form.disclaimers,
        contactFields: contactFields,
        contactLabel: state.theme_data.contact_form.title,
        contactFormSubtitle: state.theme_data.contact_form.subtitle,
        isContactFormRecaptchaEnabled: (
            Utils.selectBoxIsTrue(state.theme_data.is_contact_form_recaptcha_enabled) ||
            !!state.theme_data.contact_form.recaptcha_site_key // for backward compatibility
        ),
        isContactFormValidationSummaryDisplayed: (
            Utils.selectBoxIsTrue(state.theme_data.contact_form.is_contact_form_validation_summary_displayed)
        ),
        contactFormValidationSummaryTextFormat: (
            state.theme_data.contact_form.contact_form_validation_summary_text_format
        ),
        contactMethodLabels,
        contactOptions: contactOptions,
        contactRequirement: contactRequirement,
        contactSelectorIndex: contactSelectorIndex(contactRequirement, state.theme_data.contact_form.fields),
        formRequiredCue: (state.theme_data.labels && state.theme_data.labels.form_required_cue) || 'required',
        preferredContactMethodLabel: (state.theme_data.labels &&
            state.theme_data.labels.preferred) || 'Preferred Contact Method ',
        submissionSuccessTitle: state.theme_data.contact_form.messages &&
            state.theme_data.contact_form.messages.success && state.theme_data.contact_form.messages.success.title,
        submissionSuccess: state.theme_data.contact_form.messages &&
            state.theme_data.contact_form.messages.success && state.theme_data.contact_form.messages.success.message,
        errorMsgs: state.theme_data.contact_form.messages &&
            state.theme_data.contact_form.messages.error && state.theme_data.contact_form.messages.error,
        generalErrorMessage: (state.theme_data.contact_form.messages &&
            state.theme_data.contact_form.messages.error && state.theme_data.contact_form.messages.error.general_error)
            || "There was an error submitting your form. Please refresh and try again.",
        generalErrorMessageTitle: (state.theme_data.contact_form.messages &&
            state.theme_data.contact_form.messages.error &&
            state.theme_data.contact_form.messages.error.general_error_title)
            || "Something went wrong",
        acceptedCountryCodes: state.theme_data.contact_form.accepted_country_codes ?
            state.theme_data.contact_form.accepted_country_codes.split(/^,\/s|,/) :
            ["US"],
        relateOptIn: {
            isOptInMandatory: state.theme_data.relate_opt_in &&
                Utils.selectBoxIsTrue(state.theme_data.relate_opt_in.is_opt_in_mandatory),
            optInText: state.theme_data.relate_opt_in && state.theme_data.relate_opt_in.opt_in_text,
            url: state.org_link + 'api/relate-optin/' + state.group_id + '/'
        },
        isWellsFargo: Utils.selectBoxIsTrue(state.theme_data.is_wellsfargo),
        relateMessage: {
            isEnabled: Utils.selectBoxIsTrue(state.theme_data.post_contact_form_to_relate),
            url: `${state.org_link}api/relate-message/${state.group_id}/`
        },
        rsvpEventDetails: getRsvpEventDetails(state, selectedEventId)
    };
};

const ContactFormSelector = props => {
    if (props.amp) {
        return <AMPComponent {...props} />;
    } else {
        return <ContactForm {...props} />;
    }
};

export default connectToJSContext(mapStateToProps)(ContactFormSelector);



// WEBPACK FOOTER //
// ./components/contact_form/index.jsx